import React, { useState } from 'react';
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Tooltip,
  Fade,
  Link,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import useStore from '../store/useStore';
import CherryMark, { PetalField } from '../components/CherryMark';
import { orchard } from '../theme';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login, language, setLanguage } = useStore();

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ru' : 'en');
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    try {
      if (isRegistering) {
        const res = await fetch('/api/auth/register/parent', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password, email }),
        });

        if (res.ok) {
          setError(
            language === 'en'
              ? 'Registration successful! Please login.'
              : 'Регистрация прошла успешно! Пожалуйста, войдите.'
          );
          setIsRegistering(false);
        } else {
          const data = await res.json();
          setError(data.message || 'Registration failed');
        }
      } else {
        const res = await fetch('/api/auth/login', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });

        if (res.ok) {
          const data = await res.json();
          login(data.role, data.user);
          // back to where the link pointed (a packet /p/<code>), only inside the app
          const next = params.get('next');
          navigate(
            next && /^\/(?!\/)/.test(next) && !next.startsWith('/login')
              ? next
              : '/'
          );
        } else {
          setError(
            language === 'en'
              ? 'Login failed. Please check your credentials.'
              : 'Ошибка входа. Пожалуйста, проверьте свои данные.'
          );
        }
      }
    } catch (err) {
      setError(
        isRegistering
          ? language === 'en'
            ? 'Registration failed'
            : 'Регистрация не удалась'
          : language === 'en'
            ? 'Login failed'
            : 'Вход не удался'
      );
    }
  }

  const successText =
    language === 'en'
      ? 'Registration successful! Please login.'
      : 'Регистрация прошла успешно! Пожалуйста, войдите.';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: orchard.paper,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Soft orchard glow */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 560,
          height: 560,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${orchard.blossom} 0%, transparent 65%)`,
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          bottom: '-25%',
          left: '-12%',
          width: 620,
          height: 620,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(46,125,91,0.1) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      <PetalField />

      <Fade in timeout={900}>
        <Box sx={{ position: 'absolute', top: 26, right: 26, zIndex: 10 }}>
          <Tooltip
            title={
              language === 'en'
                ? 'Switch to Russian'
                : 'Переключить на английский'
            }
          >
            <Button
              onClick={toggleLanguage}
              startIcon={<TranslateIcon />}
              variant="outlined"
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.85)' }}
            >
              {language.toUpperCase()}
            </Button>
          </Tooltip>
        </Box>
      </Fade>

      <Container maxWidth="xs" sx={{ zIndex: 1 }}>
        <Box className="cp-stagger">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <CherryMark size={64} animated />
            <Typography
              variant="h4"
              sx={{
                mt: 1.5,
                fontFamily: orchard.fontDisplay,
                fontWeight: 700,
                fontSize: '1.6rem',
                color: orchard.ink,
              }}
            >
              Cherry-Pick{' '}
              <Box component="span" sx={{ color: orchard.cherry }}>
                CORE
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: orchard.fontMono,
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mt: 0.75,
              }}
            >
              {isRegistering
                ? language === 'en'
                  ? 'Parent account'
                  : 'Аккаунт родителя'
                : language === 'en'
                  ? 'Every student, hand-picked'
                  : 'Каждый ученик — на особом счету'}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              borderRadius: 5,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 30px 60px -24px rgba(140, 18, 48, 0.18)',
              bgcolor: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: '100%' }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                label={language === 'en' ? 'Username' : 'Имя пользователя'}
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />

              {isRegistering && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={
                    language === 'en' ? 'Email Address' : 'Электронная почта'
                  }
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                label={language === 'en' ? 'Password' : 'Пароль'}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3.5, mb: 1.5, py: 1.6, fontSize: '1rem' }}
              >
                {isRegistering
                  ? language === 'en'
                    ? 'Create account'
                    : 'Создать аккаунт'
                  : language === 'en'
                    ? 'Sign in'
                    : 'Войти'}
              </Button>

              <Button
                fullWidth
                onClick={() => setIsRegistering(!isRegistering)}
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                }}
              >
                {isRegistering
                  ? language === 'en'
                    ? 'Back to login'
                    : 'Вернуться ко входу'
                  : language === 'en'
                    ? 'Need a parent account? Register'
                    : 'Нужен аккаунт родителя? Регистрация'}
              </Button>

              {error && (
                <Fade in>
                  <Alert
                    severity={error === successText ? 'success' : 'error'}
                    sx={{ mt: 2.5, borderRadius: 3 }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}
            </Box>
          </Paper>

          <Typography sx={{ textAlign: 'center', mt: 3 }}>
            <Link
              component={RouterLink}
              to="/welcome"
              underline="hover"
              sx={{ color: 'text.secondary', fontSize: 14, fontWeight: 500 }}
            >
              {language === 'en'
                ? 'New here? See what CORE does →'
                : 'Впервые здесь? Узнайте, что умеет CORE →'}
            </Link>
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              mt: 1.5,
              fontSize: 12,
              color: 'text.disabled',
            }}
          >
            CORE · made with love by{' '}
            <Link
              href="https://klicka.ru"
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={{ color: 'inherit' }}
            >
              klicka.ru
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
